import { CrossSellingAccessoriesContainer } from '@ab-core/crosselling-accessories/container';
import { useAddedToCartAccessoriesProductList } from '@ab-core/crosselling-accessories';
import { BREAKPOINT } from '@ab-core/breakpoints';
import { getClassNames, isShopApp, useIsMobile } from '@ab-core/functions';
import { useComparableProductListState } from '@ab-core/product-comparison';
import { ComparableProductContainer } from '@ab-core/product-comparison/container';
import { getTestIdProp } from '@ab-core/testing';
import Text from '@ab-core/text';
import { globalHistory } from '@gatsbyjs/reach-router';
import type { FC, ReactNode } from 'react';
import React, { useEffect } from 'react';
import { TabStrip } from '../components/tabStrip';
import { setBottomBarUiState, useBottomBarUiState } from '../state';
import type { TabType } from '../types';
import { BarContainer, PanelContent, PanelInner, PanelWrapper, RedDot } from './style';

type TabDefinition = {
    key: TabType;
    head: ReactNode;
    content: ReactNode;
};

export const BottomBarContainer: FC = () => {
    const comparableProductList = useComparableProductListState();
    const hasComparison = comparableProductList.length > 0;
    const {
        products: accessoryProducts,
        loading: accessoryProductsLoading,
        productSku
    } = useAddedToCartAccessoriesProductList();

    const { activeTab, isMinimized, hasUnseenAccessoryChanges, allowOpeningAccessoryTabDirectly, lastSeenProductSku } =
        useBottomBarUiState();

    useEffect(() => {
        if (productSku === undefined) {
            setBottomBarUiState({
                allowOpeningAccessoryTabDirectly: true,
                lastSeenProductSku: productSku,
                hasUnseenAccessoryChanges: false
            });
        }
    }, [productSku]);

    useEffect(() => {
        if (comparableProductList.length > 0) {
            setBottomBarUiState({ activeTab: 'comparison', isMinimized: false });
        } else if (activeTab === 'comparison') {
            if (accessoryProducts.length > 0) {
                setBottomBarUiState({ activeTab: 'accessories' });
            } else {
                setBottomBarUiState({ activeTab: null, isMinimized: true });
            }
        }
    }, [comparableProductList.length]);

    useEffect(() => {
        if (accessoryProducts.length === 0 && activeTab === 'accessories') {
            setBottomBarUiState({
                activeTab: hasComparison ? 'comparison' : null,
                isMinimized: true,
                hasUnseenAccessoryChanges: false
            });

            return;
        }

        if (!accessoryProductsLoading && accessoryProducts.length > 0) {
            if (activeTab === 'accessories' && !isMinimized) {
                return;
            }

            if (allowOpeningAccessoryTabDirectly) {
                setBottomBarUiState({
                    activeTab: 'accessories',
                    isMinimized: false,
                    allowOpeningAccessoryTabDirectly: false
                });
            } else {
                setBottomBarUiState({
                    hasUnseenAccessoryChanges: productSku !== lastSeenProductSku,
                    ...(activeTab === null ? { activeTab: 'accessories' } : {})
                });
            }
        }
    }, [accessoryProductsLoading, accessoryProducts, lastSeenProductSku]);

    useEffect(() => {
        const unlisten = globalHistory.listen(() => {
            if (activeTab === 'accessories') {
                setBottomBarUiState({ isMinimized: true });
            }
        });

        return () => unlisten();
    }, [activeTab]);

    useEffect(() => {
        if (activeTab === 'accessories' && !isMinimized) {
            setBottomBarUiState({ hasUnseenAccessoryChanges: false, lastSeenProductSku: productSku });
        }
    }, [activeTab, isMinimized, productSku]);

    const onCloseAccessories = () => {
        setBottomBarUiState({ isMinimized: true });
    };

    const isVisible = !useIsMobile(BREAKPOINT.MD) && !isShopApp();

    if (!isVisible || (accessoryProducts.length === 0 && !hasComparison)) {
        return <></>;
    }

    const containerClass = getClassNames({
        prefix: 'crosselling-bar',
        modifier: { 'is-open': !isMinimized }
    });

    const tabDefinitions: TabDefinition[] = [];

    if (accessoryProducts.length > 0) {
        tabDefinitions.push({
            key: 'accessories',
            head: (
                <Text color="inherit">
                    Zubehör {!accessoryProductsLoading && `(${accessoryProducts.length})`}
                    {hasUnseenAccessoryChanges && <RedDot {...getTestIdProp('crosselling-accessories-red-dot')} />}
                </Text>
            ),
            content: <CrossSellingAccessoriesContainer products={accessoryProducts} onClose={onCloseAccessories} />
        });
    }

    if (hasComparison) {
        tabDefinitions.push({
            key: 'comparison',
            head: <Text color="inherit">Produktvergleich ({comparableProductList.length})</Text>,
            content: <ComparableProductContainer />
        });
    }

    const activeIndex = Math.max(
        0,
        tabDefinitions.findIndex((tab) => tab.key === activeTab)
    );
    const activeDefinition = tabDefinitions[activeIndex];

    const tabId = (key: TabType) => `crosselling-bar-tab-${key}`;
    const panelId = (key: TabType) => `crosselling-bar-panel-${key}`;

    const openTab = (key: TabType) => {
        setBottomBarUiState({ activeTab: key, isMinimized: false });
    };

    const inertProp = (isMinimized ? { inert: '' } : {}) as unknown as React.HTMLAttributes<HTMLDivElement>;

    return (
        <BarContainer className={containerClass} {...getTestIdProp('crosselling-bar')}>
            <TabStrip
                tabs={tabDefinitions}
                activeIndex={activeIndex}
                isMinimized={isMinimized}
                onToggleMinimize={() => setBottomBarUiState({ isMinimized: !isMinimized })}
                onSelectTab={openTab}
                tabId={tabId}
                panelId={panelId}
            />
            <PanelWrapper className={containerClass}>
                <PanelInner>
                    {activeDefinition && (
                        <PanelContent
                            role="tabpanel"
                            id={panelId(activeDefinition.key)}
                            aria-labelledby={tabId(activeDefinition.key)}
                            aria-hidden={isMinimized}
                            tabIndex={-1}
                            {...inertProp}
                        >
                            {activeDefinition.content}
                        </PanelContent>
                    )}
                </PanelInner>
            </PanelWrapper>
        </BarContainer>
    );
};
